"use client";

import Link from "next/link";
import Image from "next/image";
import axios from "axios";
import { useRouter, usePathname } from "next/navigation";
import { useCartStore } from "@/store/cart.store";
import { useAuthStore } from "@/store/auth.store";
import { useWishlistStore } from "@/store/wishlist.store";
import { ShoppingCart, User, Menu, X, Search, LogOut, Heart, ChevronDown, Phone, Play } from "lucide-react";
import { useEffect, useState, useRef, useSyncExternalStore } from "react";
import { useFocusTrap } from "@/hooks/use-focus-trap";
import { useIsClient } from "@/hooks/use-is-client";
import { useAuthModal } from "@/hooks/use-auth-modal";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { formatCurrency, mediaUrl } from "@/lib/utils";
import type { PagedResult, Product } from "@/types";

interface NavMenuItem {
  id: string;
  label: string;
  url?: string;
  target?: string;
  children: { id: string; label: string; url?: string; target?: string }[];
}

interface NavCategoryItem {
  id: string;
  name: string;
  slug: string;
  imageUrl?: string;
  focalX?: number;
  focalY?: number;
  productCount?: number;
}

interface NavbarProps {
  storeName: string;
  logoUrl?: string;
  layoutClass: string;
  navItems?: NavMenuItem[];
  categories?: NavCategoryItem[];
  storePhone?: string;
  storeEmail?: string;
  sticky?: boolean;
}

const DEFAULT_NAV: NavMenuItem[] = [
  { id: "home", label: "Ana Sayfa", url: "/", target: "_self", children: [] },
  { id: "products", label: "Ürünler", url: "/products", target: "_self", children: [] },
];

const CUSTOMER_SERVICE_LINKS = [
  { label: "Sıkça Sorulan Sorular", href: "/sikca-sorulan-sorular" },
  { label: "Yardım", href: "/yardim" },
  { label: "İletişim", href: "/iletisim" },
];

function subscribeScroll(onStoreChange: () => void) {
  window.addEventListener("scroll", onStoreChange, { passive: true });
  return () => window.removeEventListener("scroll", onStoreChange);
}

function readScrolled() {
  return window.scrollY > 20;
}

function readServerScrolled() {
  return false;
}

export function MegaSearchNavbar({ storeName, logoUrl, layoutClass, navItems, categories, storePhone, storeEmail, sticky = true }: NavbarProps) {
  const links = navItems && navItems.length > 0 ? navItems : DEFAULT_NAV;
  const router = useRouter();
  const pathname = usePathname();
  const itemCount = useCartStore((s) => s.itemCount());
  const wishlistCount = useWishlistStore((s) => s.items.length);
  const { customer, isAuthenticated, isReady, logout } = useAuthStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState<Product[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [searchSettledQuery, setSearchSettledQuery] = useState("");
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const categoriesMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useFocusTrap(mobileOpen);
  const isClient = useIsClient();
  const scrolled = useSyncExternalStore(subscribeScroll, readScrolled, readServerScrolled);
  const { openLogin, openRegister } = useAuthModal();

  const homeIndex = links.findIndex((l) => l.id === "home" || l.url === "/");
  const homeLink = homeIndex >= 0 ? links[homeIndex] : links[0];
  const remainingLinks = links.filter((_, index) => index !== (homeIndex >= 0 ? homeIndex : 0));

  function resetSearchSuggestions() {
    setSearchSuggestions([]);
    setSearchLoading(false);
    setSearchError(false);
    setSearchSettledQuery("");
  }

  function closeSearch() {
    setSearchOpen(false);
    setSearchQuery("");
    resetSearchSuggestions();
  }

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    const q = searchQuery.trim();
    closeSearch();
    setMobileOpen(false);
    if (q) router.push(`/products?q=${encodeURIComponent(q)}`);
  }

  function handleSearchOpen() {
    setSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 50);
  }

  function handleSuggestionClick(slug: string) {
    closeSearch();
    router.push(`/products/${slug}`);
  }

  function handleLogoClick(event: React.MouseEvent<HTMLAnchorElement>) {
    if (pathname === "/") {
      event.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
      closeSearch();
      setMobileOpen(false);
    }
  }

  useEffect(() => {
    if (!categoriesOpen) return;

    function handlePointerDown(event: PointerEvent) {
      if (categoriesMenuRef.current && !categoriesMenuRef.current.contains(event.target as Node)) {
        setCategoriesOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setCategoriesOpen(false);
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [categoriesOpen]);

  useEffect(() => {
    const query = searchQuery.trim();

    if (!searchOpen || !query) {
      return;
    }

    if (typeof window === "undefined" || !window.matchMedia("(min-width: 768px)").matches) {
      return;
    }

    const controller = new AbortController();

    const timeoutId = window.setTimeout(() => {
      api
        .get<PagedResult<Product>>("/api/catalog/products", {
          signal: controller.signal,
          params: {
            search: query,
            pageSize: 5,
          },
        })
        .then((response) => {
          setSearchSuggestions(response.data.items);
          setSearchError(false);
          setSearchSettledQuery(query);
        })
        .catch((error) => {
          if (axios.isCancel(error)) return;
          setSearchSuggestions([]);
          setSearchError(true);
          setSearchSettledQuery(query);
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setSearchLoading(false);
          }
        });
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [searchOpen, searchQuery]);

  useEffect(() => {
    if (!searchOpen) return;

    function dismissSearch() {
      setSearchOpen(false);
      setSearchQuery("");
      setSearchSuggestions([]);
      setSearchLoading(false);
      setSearchError(false);
    }

    function handlePointerDown(event: PointerEvent) {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        dismissSearch();
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        dismissSearch();
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [searchOpen]);

  useEffect(() => {
    if (!mobileOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setMobileOpen(false);
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileOpen]);

  const trimmedSearchQuery = searchQuery.trim();
  const hasCurrentSearchState =
    searchLoading ||
    searchError ||
    searchSuggestions.length > 0 ||
    searchSettledQuery === trimmedSearchQuery;
  const showDesktopSuggestions =
    searchOpen &&
    trimmedSearchQuery.length > 0 &&
    hasCurrentSearchState;

  return (
    <header className={`${sticky ? "fixed top-0 inset-x-0 z-40" : "relative z-10"} py-2 md:py-4 transition-colors duration-300 border-b ${scrolled ? "border-gray-300 dark:border-gray-600 shadow-[0_4px_30px_rgba(0,0,0,0.18)]" : "border-transparent"}`} style={{ backgroundColor: "var(--sf-nav-bg)" }}>
      <div className={`${layoutClass} mx-auto`}>
        {/* Utility Top Bar */}
        <div className="hidden md:flex items-center justify-between py-1.5 text-xs border-b border-gray-200/60 dark:border-white/10">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1 text-gray-500">
              <Phone className="h-3 w-3" />
              {storePhone ? (
                <a href={`tel:${storePhone.replace(/\s+/g, "")}`} className="hover:text-gray-800 dark:hover:text-gray-200 transition-colors">
                  {storePhone}
                </a>
              ) : (
                <Link href="/musteri-hizmetleri" className="hover:text-gray-800 dark:hover:text-gray-200 transition-colors">
                  Müşteri Hizmetleri
                </Link>
              )}
            </span>
            {storeEmail && (
              <>
                <span className="text-gray-400">|</span>
                <a href={`mailto:${storeEmail}`} className="text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 transition-colors">
                  {storeEmail}
                </a>
              </>
            )}
          </div>
          <div className="flex items-center gap-4">
            <Link href="/account" className="text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 transition-colors">Sipariş Takip</Link>
            {CUSTOMER_SERVICE_LINKS.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between h-14 gap-2 md:h-[75px] md:gap-8">
          {/* Logo */}
          <Link href="/" onClick={handleLogoClick} className="flex items-center gap-2 shrink-0">
            {logoUrl ? (
              <Image src={logoUrl} alt={storeName} height={75} width={300} quality={90} className="h-12 w-auto max-w-[150px] object-contain md:h-[75px] md:max-w-none" />
            ) : (
              <span className="text-lg font-bold tracking-tight text-gray-950 uppercase sm:text-xl">{storeName}</span>
            )}
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8 flex-1 justify-center">
            {homeLink && (() => {
              const href = homeLink.url ?? "/";
              const isActive = href === "/" ? pathname === "/" : pathname.startsWith(href);
              return (
                <Link
                  key={homeLink.id}
                  href={href}
                  target={homeLink.target ?? "_self"}
                  onClick={() => setCategoriesOpen(false)}
                  className="group relative text-sm font-semibold transition-colors tracking-wide pb-1 cursor-pointer"
                  style={{
                    fontFamily: "var(--sf-font-heading, sans-serif)",
                    color: isActive ? "var(--sf-nav-active)" : "var(--sf-nav-link)",
                  }}
                >
                  {homeLink.label}
                  <span
                    className={`absolute bottom-0 left-0 right-0 h-0.5 origin-left rounded-full transition-transform duration-300 ease-out ${isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`}
                    style={{ backgroundColor: "var(--sf-nav-underline)" }}
                  />
                </Link>
              );
            })()}

            {categories && categories.length > 0 && (
              <div ref={categoriesMenuRef} className="relative">
                <button
                  type="button"
                  aria-expanded={categoriesOpen}
                  aria-controls="desktop-category-menu"
                  onClick={() => setCategoriesOpen((value) => !value)}
                  className="group relative flex items-center gap-1 text-sm font-semibold transition-colors tracking-wide pb-1 cursor-pointer"
                  style={{
                    fontFamily: "var(--sf-font-heading, sans-serif)",
                    color: "var(--sf-nav-link)",
                  }}
                >
                  Kategoriler
                  <ChevronDown className={`h-3.5 w-3.5 opacity-70 transition-transform duration-300 ${categoriesOpen ? "rotate-180" : ""}`} />
                  <span
                    className={`absolute bottom-0 left-0 right-0 h-0.5 origin-left rounded-full transition-transform duration-300 ease-out ${categoriesOpen ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`}
                    style={{ backgroundColor: "var(--sf-nav-underline)" }}
                  />
                </button>
                {categoriesOpen && (
                  <div id="desktop-category-menu" className="absolute top-full left-0 pt-2 z-20">
                    <div className="w-[min(90vw,640px)] max-h-[70vh] overflow-y-auto rounded-xl border border-gray-200 bg-white p-4 shadow-xl dark:border-gray-700 dark:bg-gray-900">
                      <Link
                        href="/products"
                        onClick={() => setCategoriesOpen(false)}
                        className="group mb-3 inline-flex items-center gap-1.5 text-sm font-semibold text-gray-900 hover:underline dark:text-gray-100"
                      >
                        Tüm kategoriler
                        <Play className="h-2.5 w-2.5 fill-current stroke-0 transition-transform group-hover:translate-x-0.5" />
                      </Link>
                      <div className="grid grid-cols-2 gap-2 lg:grid-cols-3">
                        {categories.map((cat) => (
                          <Link
                            key={cat.id}
                            href={`/kategori/${cat.slug}`}
                            onClick={() => setCategoriesOpen(false)}
                            className="group flex items-center gap-3 rounded-lg p-2 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
                          >
                            <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-gray-100 ring-1 ring-gray-200 dark:bg-white/5 dark:ring-white/10">
                              {cat.imageUrl ? (
                                <Image
                                  src={mediaUrl(cat.imageUrl)}
                                  alt={cat.name}
                                  fill
                                  sizes="48px"
                                  className="object-cover"
                                  style={{ objectPosition: `${cat.focalX ?? 50}% ${cat.focalY ?? 50}%` }}
                                />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center">
                                  <svg className="h-5 w-5 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                  </svg>
                                </div>
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-gray-800 group-hover:text-gray-950 dark:text-gray-200 dark:group-hover:text-white">
                                {cat.name}
                              </p>
                              {cat.productCount != null && (
                                <p className="text-xs text-gray-400">{cat.productCount} ürün</p>
                              )}
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {remainingLinks.map((l) => {
              const href = l.url ?? "/";
              const isActive = href === "/" ? pathname === "/" : pathname.startsWith(href);
              return (
                <Link
                  key={l.id}
                  href={href}
                  target={l.target ?? "_self"}
                  onClick={() => setCategoriesOpen(false)}
                  className="group relative text-sm font-semibold transition-colors tracking-wide pb-1 cursor-pointer"
                  style={{
                    fontFamily: "var(--sf-font-heading, sans-serif)",
                    color: isActive ? "var(--sf-nav-active)" : "var(--sf-nav-link)",
                  }}
                >
                  {l.label}
                  <span
                    className={`absolute bottom-0 left-0 right-0 h-0.5 origin-left rounded-full transition-transform duration-300 ease-out ${isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`}
                    style={{ backgroundColor: "var(--sf-nav-underline)" }}
                  />
                </Link>
              );
            })}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-0.5 md:gap-1">
            {/* Search */}
            {searchOpen ? (
              <div ref={searchContainerRef} className="relative">
                <form id="desktop-search-form" onSubmit={handleSearchSubmit} className="flex items-center gap-1">
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      const nextValue = e.target.value;
                      setSearchQuery(nextValue);
                      if (!nextValue.trim()) {
                        resetSearchSuggestions();
                      } else {
                        setSearchLoading(true);
                        setSearchError(false);
                        setSearchSettledQuery("");
                      }
                    }}
                    onKeyDown={(e) => e.key === "Escape" && closeSearch()}
                    placeholder="Ürün ara..."
                    className="w-56 md:w-80 lg:w-96 h-9 px-3 text-sm border border-gray-300 dark:border-white/20 dark:bg-white/10 dark:text-gray-100 dark:placeholder:text-gray-500 rounded-l-md focus:outline-none focus:border-[var(--sf-primary)] focus:ring-1 focus:ring-[var(--sf-primary)]"
                  />
                  <button
                    type="submit"
                    className="h-9 px-3 bg-[var(--sf-primary)] text-white rounded-r-md hover:opacity-90 transition-opacity"
                    aria-label="Ara"
                  >
                    <Search className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={closeSearch}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Kapat"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </form>

                {showDesktopSuggestions && (
                  <div className="absolute left-0 right-10 top-full z-30 mt-2 hidden overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-white/10 dark:bg-gray-950 md:block">
                    <div className="max-h-[28rem] overflow-y-auto py-2">
                      {searchLoading ? (
                        <div className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400">Aranıyor...</div>
                      ) : searchError ? (
                        <div className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400">Sonuçlar yüklenemedi.</div>
                      ) : searchSuggestions.length === 0 ? (
                        <div className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400">Sonuç bulunamadı.</div>
                      ) : (
                        searchSuggestions.map((product) => (
                          <button
                            key={product.id}
                            type="button"
                            onClick={() => handleSuggestionClick(product.slug)}
                            className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-white/5"
                          >
                            <div className="relative h-14 w-14 overflow-hidden rounded-xl bg-gray-100 dark:bg-white/5">
                              {product.coverImageUrl ? (
                                <Image
                                  src={mediaUrl(product.coverImageUrl)}
                                  alt={product.name}
                                  fill
                                  sizes="56px"
                                  className="object-cover"
                                />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center text-[11px] text-gray-400">Görsel yok</div>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{product.name}</div>
                              <div className="mt-1 text-sm font-semibold" style={{ color: "var(--sf-card-price, var(--sf-primary))" }}>
                                {formatCurrency(product.price)}
                              </div>
                            </div>
                          </button>
                        ))
                      )}

                      <div className="mt-2 border-t border-gray-100 px-2 pt-2 dark:border-white/10">
                        <button
                          type="submit"
                          form="desktop-search-form"
                          className="group inline-flex w-full items-center gap-1.5 rounded-xl px-3 py-2 text-left text-sm font-semibold text-[var(--sf-primary)] transition-colors hover:bg-[color:var(--sf-primary-soft,#f5f5f5)]"
                        >
                          Tüm sonuçları gör
                          <Play className="h-2.5 w-2.5 fill-current stroke-0 transition-transform group-hover:translate-x-0.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={handleSearchOpen}
                className="flex min-h-10 min-w-10 items-center justify-center rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 md:min-h-[44px] md:min-w-[44px] md:p-2.5"
                aria-label="Ara"
              >
                <Search className="h-5 w-5" />
              </button>
            )}

            {/* Wishlist */}
            <Link
              href="/account/wishlist"
              className="relative flex min-h-10 min-w-10 items-center justify-center p-2 text-slate-500 transition-colors hover:text-slate-900 md:min-h-[44px] md:min-w-[44px] md:p-2.5"
              aria-label="Favorilerim"
            >
              <Heart className="h-5 w-5" />
              {isClient && wishlistCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold text-white bg-red-500">
                  {wishlistCount > 9 ? "9+" : wishlistCount}
                </span>
              )}
            </Link>

            {/* Cart */}
            <Link
              href="/cart"
              data-cart-icon
              className="relative flex min-h-10 min-w-10 items-center justify-center p-2 text-slate-500 transition-colors hover:text-slate-900 md:min-h-[44px] md:min-w-[44px] md:p-2.5"
              aria-label={`Sepet${isClient && itemCount > 0 ? ` (${itemCount} ürün)` : ""}`}
            >
              <motion.span
                key={isClient ? itemCount : "cart"}
                className="inline-flex"
                animate={isClient && itemCount > 0 ? { scale: [1, 1.18, 1] } : { scale: 1 }}
                transition={{ duration: 0.25 }}
              >
                <ShoppingCart className="h-5 w-5" />
              </motion.span>
              {isClient && itemCount > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold text-white animate-cart-bounce"
                  style={{ backgroundColor: "var(--sf-accent)" }}
                >
                  {itemCount > 9 ? "9+" : itemCount}
                </span>
              )}
            </Link>

            {/* User — desktop. Mount öncesi invisible (yer kaplar ama görünmez) → layout shift olmaz */}
            <div className={`hidden md:flex items-center gap-1 transition-none ${isClient ? "" : "invisible"}`}>
              {isReady && isAuthenticated ? (
                <>
                  <Link
                    href="/account"
                    className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <User className="h-4 w-4" />
                    {customer?.fullName?.split(" ")[0]}
                  </Link>
                  <button
                    onClick={() => void logout()}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                    aria-label="Çıkış Yap"
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </>
              ) : (
                <Link
                  href="/login"
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <User className="h-4 w-4" />
                  Giriş
                </Link>
              )}
            </div>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="flex min-h-10 min-w-10 items-center justify-center rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 md:hidden"
              aria-label={mobileOpen ? "Menüyü Kapat" : "Menüyü Aç"}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              key="mobile-menu-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-50 md:hidden bg-black/45"
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              key="mobile-menu-panel"
              ref={(el) => { mobileMenuRef.current = el as HTMLElement | null; }}
              role="dialog"
              aria-modal="true"
              aria-label="Mobil Menü"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.24, ease: "easeOut" }}
              className="fixed right-0 top-0 flex h-dvh w-[min(86vw,360px)] flex-col border-l border-gray-100 bg-white shadow-2xl md:hidden z-[60] dark:border-white/10 dark:bg-gray-950"
            >
              <div className="flex h-16 shrink-0 items-center justify-between border-b border-gray-100 px-4 dark:border-white/10">
                <span className="text-sm font-bold uppercase tracking-tight text-gray-950 dark:text-gray-100">{storeName}</span>
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/10"
                  aria-label="Menüyü kapat"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4">
                {/* Mobile Search */}
                <form
                  onSubmit={handleSearchSubmit}
                  className="mb-4 flex items-center"
                >
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Ürün ara..."
                    aria-label="Ürün ara"
                    className="h-11 min-w-0 flex-1 rounded-l-md border border-gray-300 px-3 text-sm focus:border-[var(--sf-primary)] focus:outline-none dark:border-white/10 dark:bg-white/5"
                  />
                  <button
                    type="submit"
                    className="h-11 px-3 bg-[var(--sf-primary)] text-white rounded-r-md"
                    aria-label="Ara"
                  >
                    <Search className="h-4 w-4" />
                  </button>
                </form>

                <nav className="space-y-1">
                  {links.map((l) => (
                    <Link
                      key={l.id}
                      href={l.url ?? "/"}
                      target={l.target ?? "_self"}
                      onClick={() => setMobileOpen(false)}
                      className="block rounded-md px-3 py-3 text-base font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/10"
                    >
                      {l.label}
                    </Link>
                  ))}
                </nav>

                {categories && categories.length > 0 && (
                  <div className="mt-4 border-t border-gray-100 pt-3 dark:border-white/10">
                    <p className="px-3 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Kategoriler</p>
                    <div className="space-y-1">
                      <Link
                        href="/products"
                        onClick={() => setMobileOpen(false)}
                        className="group flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/10"
                      >
                        Tüm kategoriler
                        <Play className="h-2.5 w-2.5 fill-current stroke-0 transition-transform group-hover:translate-x-0.5" />
                      </Link>
                      {categories.map((cat) => (
                        <Link
                          key={cat.id}
                          href={`/kategori/${cat.slug}`}
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-3 rounded-md px-3 py-2 transition-colors hover:bg-gray-100 dark:hover:bg-white/10"
                        >
                          <div className="relative h-9 w-9 shrink-0 overflow-hidden rounded-md bg-gray-100 ring-1 ring-gray-200 dark:bg-white/5 dark:ring-white/10">
                            {cat.imageUrl ? (
                              <Image
                                src={mediaUrl(cat.imageUrl)}
                                alt={cat.name}
                                fill
                                sizes="36px"
                                className="object-cover"
                                style={{ objectPosition: `${cat.focalX ?? 50}% ${cat.focalY ?? 50}%` }}
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center">
                                <svg className="h-4 w-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                              </div>
                            )}
                          </div>
                          <span className="truncate text-sm font-medium text-gray-700 dark:text-gray-200">{cat.name}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-4 space-y-1 border-t border-gray-100 pt-3 dark:border-white/10">
                  {isClient && isReady && isAuthenticated ? (
                    <>
                      <Link
                        href="/account"
                        onClick={() => setMobileOpen(false)}
                        className="block rounded-md px-3 py-3 text-base font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/10"
                      >
                        Hesabım
                      </Link>
                      <button
                        onClick={() => {
                          void logout();
                          setMobileOpen(false);
                        }}
                        className="block w-full rounded-md px-3 py-3 text-left text-base font-medium text-red-500 transition-colors hover:bg-red-50 dark:hover:bg-red-500/10"
                      >
                        Çıkış Yap
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => { openLogin(); setMobileOpen(false); }}
                        className="block w-full rounded-md px-3 py-3 text-left text-base font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/10"
                      >
                        Giriş Yap
                      </button>
                      <button
                        onClick={() => { openRegister(); setMobileOpen(false); }}
                        className="block w-full rounded-md px-3 py-3 text-left text-base font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/10"
                      >
                        Üye Ol
                      </button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}
